import {Three as THREE, ThreeTsl as TSL} from "@cm/material-nodes/three"
import {ToneMapping} from "@cm/template-nodes"
import {buildLUTEntries, ToneMappingFunctions} from "@cm/image-processing/operations/tone-mapping"
import {DEFAULT_FLOAT_TEXTURE_TYPE, Float16ArrayBuilder, Float32ArrayBuilder, ThreeFloatTextureType} from "@app/template-editor/helpers/three-utils"
import {asVec3} from "@cm/material-nodes"

const USE_TETRAHEDRAL_INTERPOLATION = true

export class ToneMappingRenderPass extends THREE.TempNode<"vec4"> {
    private toneMapLUT = new CubeLUTTexture(
        32,
        2.0,
        DEFAULT_FLOAT_TEXTURE_TYPE,
        USE_TETRAHEDRAL_INTERPOLATION ? THREE.NearestFilter : THREE.LinearFilter,
        USE_TETRAHEDRAL_INTERPOLATION ? THREE.NearestFilter : THREE.LinearFilter,
    )
    private toneMapLUTNode = TSL.texture(this.toneMapLUT)
    private toneMappingType!: ToneMapping
    private exposure = TSL.uniform(1.0)
    private applyToneMapping = TSL.uniform(false)

    constructor(
        toneMapping: ToneMapping,
        exposure: number,
        private input: THREE.Node<"vec4">,
    ) {
        super("vec4")

        this.setToneMapping(toneMapping)
        this.setExposure(exposure)
    }

    override dispose() {
        this.toneMapLUT.dispose()
    }

    override setup() {
        const resolve = TSL.Fn(() => {
            const result = TSL.vec4(0.0).toVar()

            TSL.If(TSL.not(this.applyToneMapping).or(this.input.a.lessThanEqual(0.0)), () => {
                result.assign(this.input)
            }).Else(() => {
                const position = this.input.rgb.div(this.input.a) // un-premultiply alpha
                const exposed = position.mul(this.exposure)
                const toneMapped = asVec3(TSL.sRGBTransferOETF(exposed))

                const sz = this.toneMapLUT.size
                const sz_1 = sz - 1.0
                const rsz = 1.0 / sz
                const rsz2 = rsz * rsz
                const toneMapLUTRangeScale = 1.0 / this.toneMapLUT.range

                let toneMappedLut: THREE.Node<"vec3">

                if (USE_TETRAHEDRAL_INTERPOLATION) {
                    const getLUTValue = TSL.Fn(([xi, yi, zi]: [xi: THREE.Node<"float">, yi: THREE.Node<"float">, zi: THREE.Node<"float">]) => {
                        const u = xi.mul(rsz)
                        const v = yi.add(zi.mul(sz)).mul(rsz2)
                        return this.toneMapLUTNode.sample(TSL.vec2(u, v)).rgb
                    })

                    const tetrahedralSample = TSL.Fn(([coord]: [coord: THREE.Node<"vec3">]) => {
                        const x = coord.x.mul(sz_1)
                        const y = coord.y.mul(sz_1)
                        const z = coord.z.mul(sz_1)

                        const x0 = TSL.clamp(TSL.floor(x), 0.0, sz_1)
                        const y0 = TSL.clamp(TSL.floor(y), 0.0, sz_1)
                        const z0 = TSL.clamp(TSL.floor(z), 0.0, sz_1)
                        const x1 = TSL.clamp(x0.add(1.0), 0.0, sz_1)
                        const y1 = TSL.clamp(y0.add(1.0), 0.0, sz_1)
                        const z1 = TSL.clamp(z0.add(1.0), 0.0, sz_1)

                        const xf = x.sub(TSL.floor(x))
                        const yf = y.sub(TSL.floor(y))
                        const zf = z.sub(TSL.floor(z))

                        // Fetch all 8 corner values
                        const c000 = getLUTValue(x0, y0, z0)
                        const c001 = getLUTValue(x0, y0, z1)
                        const c010 = getLUTValue(x0, y1, z0)
                        const c011 = getLUTValue(x0, y1, z1)
                        const c100 = getLUTValue(x1, y0, z0)
                        const c101 = getLUTValue(x1, y0, z1)
                        const c110 = getLUTValue(x1, y1, z0)
                        const c111 = getLUTValue(x1, y1, z1)

                        const resultColor = TSL.vec3(0.0).toVar()

                        // Tetrahedral interpolation based on which tetrahedron the point falls into
                        TSL.If(xf.greaterThanEqual(yf), () => {
                            TSL.If(yf.greaterThanEqual(zf), () => {
                                // xf >= yf >= zf
                                resultColor.assign(c000.add(c100.sub(c000).mul(xf)).add(c110.sub(c100).mul(yf)).add(c111.sub(c110).mul(zf)))
                            }).Else(() => {
                                TSL.If(xf.greaterThanEqual(zf), () => {
                                    // xf >= zf > yf
                                    resultColor.assign(c000.add(c100.sub(c000).mul(xf)).add(c101.sub(c100).mul(zf)).add(c111.sub(c101).mul(yf)))
                                }).Else(() => {
                                    // zf > xf >= yf
                                    resultColor.assign(c000.add(c001.sub(c000).mul(zf)).add(c101.sub(c001).mul(xf)).add(c111.sub(c101).mul(yf)))
                                })
                            })
                        }).Else(() => {
                            TSL.If(zf.greaterThanEqual(yf), () => {
                                // zf >= yf > xf
                                resultColor.assign(c000.add(c001.sub(c000).mul(zf)).add(c011.sub(c001).mul(yf)).add(c111.sub(c011).mul(xf)))
                            }).Else(() => {
                                TSL.If(zf.greaterThanEqual(xf), () => {
                                    // yf > zf >= xf
                                    resultColor.assign(c000.add(c010.sub(c000).mul(yf)).add(c011.sub(c010).mul(zf)).add(c111.sub(c011).mul(xf)))
                                }).Else(() => {
                                    // yf > xf > zf
                                    resultColor.assign(c000.add(c010.sub(c000).mul(yf)).add(c110.sub(c010).mul(xf)).add(c111.sub(c110).mul(zf)))
                                })
                            })
                        })

                        return resultColor
                    })

                    toneMappedLut = tetrahedralSample(toneMapped.mul(toneMapLUTRangeScale))
                } else {
                    const lutCoordConstrain = TSL.Fn(([x, coord]: [x: THREE.Node<"float">, coord: THREE.Node<"float">]) => {
                        const below = TSL.lessThan(coord, 0.0)
                        const above = TSL.greaterThan(coord, 1.0)
                        return TSL.select(below, 0.0, TSL.select(above, 1.0, x))
                    })

                    const trilinearSample = TSL.Fn(([coord]: [coord: THREE.Node<"vec3">]) => {
                        const tx = coord.x.mul(sz_1).add(0.5).mul(rsz)
                        const ty = TSL.clamp(coord.y, 0.0, 1.0).mul(sz_1).add(0.5).mul(rsz2)
                        const _iz = coord.z.mul(sz_1)
                        const iz = TSL.floor(_iz)
                        const zFrac = _iz.sub(iz)
                        const tyz0 = ty.add(TSL.clamp(iz, 0.0, sz_1).mul(rsz))
                        const tyz1 = ty.add(TSL.clamp(iz.add(1.0), 0.0, sz_1).mul(rsz))
                        const z0c = this.toneMapLUTNode.sample(TSL.vec2(tx, tyz0))
                        const z1c = this.toneMapLUTNode.sample(TSL.vec2(tx, tyz1))
                        const sampleResult = TSL.mix(z0c, z1c, zFrac)
                        return TSL.vec4(
                            lutCoordConstrain(sampleResult.r, coord.x),
                            lutCoordConstrain(sampleResult.g, coord.y),
                            lutCoordConstrain(sampleResult.b, coord.z),
                            sampleResult.a,
                        )
                    })

                    toneMappedLut = trilinearSample(toneMapped.mul(toneMapLUTRangeScale)).rgb
                }

                const finalColor = TSL.vec4(toneMappedLut.mul(this.input.a), this.input.a) // re-premultiply alpha
                result.assign(finalColor)
            })

            return result
        })

        return resolve()
    }

    setToneMapping(toneMapping: ToneMapping) {
        this.toneMappingType = toneMapping
        this.toneMapLUT.updateWithTonemapping(toneMapping)
        this.autoEnable()
    }

    setExposure(exposure: number) {
        this.exposure.value = exposure
        this.autoEnable()
    }

    private autoEnable() {
        this.applyToneMapping.value = this.toneMappingType.mode !== "linear" || this.exposure.value !== 1.0
    }
}

class CubeLUTTexture extends THREE.DataTexture {
    private data: Float16ArrayBuilder | Float32ArrayBuilder
    readonly size: number
    readonly range: number

    constructor(
        size: number,
        range: number,
        type: ThreeFloatTextureType,
        magFilter: THREE.MagnificationTextureFilter,
        minFilter: THREE.MinificationTextureFilter,
    ) {
        const ArrayBuilder = type === THREE.HalfFloatType ? Float16ArrayBuilder : Float32ArrayBuilder
        const data = new ArrayBuilder(size * size * size * 4)
        super(data.array, size, size * size, THREE.RGBAFormat, type)
        this.data = data
        this.size = size
        this.range = range
        this.wrapS = THREE.ClampToEdgeWrapping
        this.wrapT = THREE.ClampToEdgeWrapping
        this.minFilter = minFilter
        this.magFilter = magFilter
    }

    updateWithArray(array: Float32Array) {
        const numElements = array.length / 3
        for (let n = 0; n < numElements; n++) {
            this.data.set(n * 4 + 0, array[n * 3 + 0])
            this.data.set(n * 4 + 1, array[n * 3 + 1])
            this.data.set(n * 4 + 2, array[n * 3 + 2])
            this.data.set(n * 4 + 3, 1.0)
        }
        this.needsUpdate = true
    }

    updateWithTonemapping(toneMapping: ToneMapping) {
        // Sample in sRGB space, to utilize the range of the LUT better
        this.updateWithArray(buildLUTEntries(this.size, this.range, ToneMappingFunctions.createForToneMappingData(toneMapping), true, false))
    }
}
